import { forwardRef, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import { scrollToElement, focusField } from '@/utils/focus';

import { ClientFormStepper } from './ClientFormStepper';
import { ClientReviewStep } from './ClientReviewStep';
import { BasicsStep } from './steps/BasicsStep';
import { RolesStep } from './steps/RolesStep';
import { MultipliersStep } from './steps/MultipliersStep';
import { BillingStep, billingSubSteps } from './steps/BillingStep';

import type { ClientDisplayData } from '@/pages/ClientsPage';
import type { UserConfigurationState } from '@/types/userConfiguration';
import { type ClientFormData, getDefaultsToRestore } from './utilities/formDataUtils';
import type { CustomizedSections } from './utilities/formDataUtils';

import { ValidationProvider, ValidationFooter, useValidationToasts } from '@/components/validation';

// Import hooks
import { useClientFormState } from './hooks/useClientFormState';
import { useClientFormSteps } from './hooks/useClientFormSteps';
import { useClientFormValidation } from './hooks/useClientFormValidation';
import { useClientFormHandlers } from './hooks/useClientFormHandlers';
import { useClientSchemaMap } from './hooks/useClientSchemaMap';

interface ClientFormProps {
  clientData: ClientFormData;
  onSave: (updatedData: Partial<ClientFormData>) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string | null;
  isEditing?: boolean;
  onRemoveFromForm?: () => void;
  className?: string;
  /** Called whenever the Name field changes (or is initialised) */
  onNameChange?: (name: string) => void;
  allClients?: ClientDisplayData[];
}

/** Props passed from outer to inner component (lifted state). */
interface ClientFormInnerProps extends ClientFormProps {
  formData: ClientFormData;
  setFormData: (data: ClientFormData | ((prev: ClientFormData) => ClientFormData)) => void;
  isTravelManuallyEnabled: boolean;
  setIsTravelManuallyEnabled: (enabled: boolean) => void;
  isAiGeneratedDescription: boolean;
  setIsAiGeneratedDescription: (value: boolean) => void;
  userDefaults: UserConfigurationState | undefined;
  isLoadingDefaults: boolean;
  granularCustomizedFields: Record<string, boolean>;
  customizedSections: CustomizedSections;
}

/** Inner component that consumes ValidationContext. */
const ClientFormInner = forwardRef<HTMLDivElement, ClientFormInnerProps>((props, ref) => {
  const {
    onSave,
    onCancel,
    isLoading = false,
    error = null,
    isEditing = false,
    onRemoveFromForm,
    className,
    onNameChange,
    allClients = [],
    formData,
    setFormData,
    isTravelManuallyEnabled,
    setIsTravelManuallyEnabled,
    isAiGeneratedDescription,
    setIsAiGeneratedDescription,
    userDefaults,
    isLoadingDefaults,
    granularCustomizedFields,
    customizedSections,
  } = props;

  // Emit toast notifications for active errors
  useValidationToasts();

  const {
    steps,
    currentStep,
    setCurrentStep,
    completedSteps,
    setCompletedSteps,
    activeSubStepId,
  } = useClientFormSteps();

  const {
    isRolesStepValid,
    setIsRolesStepValid,
    isMultipliersStepValid,
    setIsMultipliersStepValid,
    isNextButtonDisabled,
    isNextButtonLoading,
    bridgeErrors,
    bridgeTouchedFields,
    ctx,
  } = useClientFormValidation({
    currentStep,
    isLoading,
    isLoadingDefaults,
    isEditing,
  });

  const {
    handleChange,
    handleFieldBlurWrapper,
    handleClearFieldValidationWrapper,
    handleSwitchChange,
    handleRevertSection,
    handleNext,
    handleBackWrapper,
    handleSave,
  } = useClientFormHandlers({
    formData,
    setFormData,
    customizedSections,
    isTravelManuallyEnabled,
    setIsTravelManuallyEnabled,
    allClients,
    isEditing,
    currentStep,
    setCurrentStep,
    setCompletedSteps,
    steps,
    isRolesStepValid,
    userDefaults: userDefaults ?? null,
    onNameChange,
    onSave,
    ctx,
  });

  // Derive section-level customization for badges and revert buttons
  const granularCustomized = useMemo(() => {
    const defaults = userDefaults ?? {} as UserConfigurationState;
    const dayRateFields = Object.keys(getDefaultsToRestore('dayRate', defaults));
    const travelFields = Object.keys(getDefaultsToRestore('travel', defaults));
    const invoiceFields = Object.keys(getDefaultsToRestore('invoice', defaults));
    const rolesFields = Object.keys(getDefaultsToRestore('roles', defaults));
    const multipliersFields = Object.keys(getDefaultsToRestore('multipliers', defaults));
    const shortTurnFields = Object.keys(getDefaultsToRestore('shortTurn', defaults));
    const holidayFields = Object.keys(getDefaultsToRestore('holiday', defaults));
    const mealBreakFields = Object.keys(getDefaultsToRestore('mealBreak', defaults));
    const overnightFields = Object.keys(getDefaultsToRestore('overnight', defaults));
    const breaksFields = Object.keys(getDefaultsToRestore('breaks', defaults));
    const expensesFields = Object.keys(getDefaultsToRestore('expenses', defaults));
    const gearFields = Object.keys(getDefaultsToRestore('gear', defaults));

    return {
      breaks: breaksFields.some(field => granularCustomizedFields[field as keyof ClientFormData]),
      dayRate: dayRateFields.some(field => granularCustomizedFields[field as keyof ClientFormData]),
      travel: travelFields.some(field => granularCustomizedFields[field as keyof ClientFormData]),
      invoice: invoiceFields.some(field => granularCustomizedFields[field as keyof ClientFormData]),
      roles: rolesFields.some(field => granularCustomizedFields[field as keyof ClientFormData]),
      multipliers: multipliersFields.some(field => granularCustomizedFields[field as keyof ClientFormData]),
      shortTurn: shortTurnFields.some(field => granularCustomizedFields[field as keyof ClientFormData]),
      holiday: holidayFields.some(field => granularCustomizedFields[field as keyof ClientFormData]),
      mealBreak: mealBreakFields.some(field => granularCustomizedFields[field as keyof ClientFormData]),
      overnight: overnightFields.some(field => granularCustomizedFields[field as keyof ClientFormData]),
      expenses: expensesFields.some(field => granularCustomizedFields[field as keyof ClientFormData]),
      gear: gearFields.some(field => granularCustomizedFields[field as keyof ClientFormData]),
    };
  }, [granularCustomizedFields, userDefaults]);

  const stepScope = String(currentStep);
  const hasSchemaErrors = ctx.getActiveErrors(stepScope).length > 0;

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basics
        return <BasicsStep
          formData={formData}
          onUpdate={handleChange}
          onFieldBlur={handleFieldBlurWrapper}
          isLoading={isLoading || (isLoadingDefaults && !isEditing)}
          errors={bridgeErrors}
          touchedFields={bridgeTouchedFields}
          isAiGenerated={isAiGeneratedDescription}
          setIsAiGenerated={setIsAiGeneratedDescription}
        />;
      case 1: // Roles
        return <RolesStep
          formData={formData}
          onRolesChange={(newRoles) => handleChange('roles', newRoles)}
          isCustomized={granularCustomized.roles}
          onRevert={() => handleRevertSection('roles')}
          userDefaultRoles={userDefaults?.roles}
          errors={bridgeErrors}
          touchedFields={bridgeTouchedFields}
          onValidityChange={setIsRolesStepValid}
          clientName={formData.name ?? undefined}
          // Props for DayRateConfig
          handleChange={handleChange}
          handleSwitchChange={handleSwitchChange}
          onFieldBlur={handleFieldBlurWrapper}
          isLoading={isLoading}
          isDayRateCustomized={granularCustomized.dayRate}
          onRevertDayRate={() => handleRevertSection('dayRate')}
        />;
      case 2: // Multipliers
        return <MultipliersStep
          formData={formData}
          handleChange={handleChange}
          handleSwitchChange={handleSwitchChange}
          onFieldBlur={handleFieldBlurWrapper}
          isLoading={isLoading}
          errors={bridgeErrors}
          touchedFields={bridgeTouchedFields}
          granularCustomized={{
            multipliers: granularCustomized.multipliers,
            shortTurn: granularCustomized.shortTurn,
            holiday: granularCustomized.holiday,
            mealBreak: granularCustomized.mealBreak,
            overnight: granularCustomized.overnight,
          }}
          onRevertSection={handleRevertSection}
          userDefaults={userDefaults ?? undefined}
          granularCustomizedFields={granularCustomizedFields}
          onMultipliersValidityChange={setIsMultipliersStepValid}
        />;
      case 3: // Billing
        return <BillingStep
          formData={formData}
          handleChange={handleChange}
          handleSwitchChange={handleSwitchChange}
          onFieldBlur={handleFieldBlurWrapper}
          onClearFieldValidation={handleClearFieldValidationWrapper}
          isLoading={isLoading}
          errors={bridgeErrors}
          touchedFields={bridgeTouchedFields}
          isTravelManuallyEnabled={isTravelManuallyEnabled}
          setIsTravelManuallyEnabled={setIsTravelManuallyEnabled}
          granularCustomized={granularCustomized}
          onRevertSection={handleRevertSection}
          userDefaults={userDefaults ?? undefined}
        />;
      case 4: // Review
        return (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-foreground">Review Client Details</h4>
            <p className="text-sm text-muted-foreground">
              Review the information below before saving the client.
            </p>
            <div className="max-h-[60vh] overflow-y-auto rounded-md p-1 -m-1">
              <ClientReviewStep
                formData={formData}
                customizedSections={{
                  ...customizedSections,
                  overnight: granularCustomized.overnight
                }}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div ref={ref} className={cn('w-full', className)}>
      <div className="flex flex-col md:flex-row gap-8">
        <ClientFormStepper
          steps={steps}
          currentStep={currentStep}
          setStep={setCurrentStep}
          completedSteps={completedSteps}
          billingSubSteps={billingSubSteps}
          onSubStepClick={scrollToElement}
          activeSubStepId={activeSubStepId}
          isEditing={isEditing}
          clientName={formData.name ?? undefined}
        />
        <main className="flex-1 min-w-0">
          <div className="space-y-6">
            {renderStepContent()}
            {error && <p className="text-sm text-destructive mt-4">{error}</p>}
          </div>
        </main>
      </div>
      <div className="flex justify-between items-center gap-2 pt-6 mt-6 border-t">
        <div>
          {isEditing && onRemoveFromForm && (
            <Button variant="destructive" onClick={onRemoveFromForm} disabled={isLoading}>
              Delete
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>Cancel</Button>
          {currentStep > 0 && (
            <Button variant="outline" onClick={handleBackWrapper} disabled={isLoading}>Back</Button>
          )}

          {/* Validation footer hint between nav buttons */}
          {hasSchemaErrors && currentStep < steps.length - 1 && (
            <div className="basis-full order-first min-w-0 px-1 sm:order-none sm:flex-1">
              <ValidationFooter scope={stepScope} />
            </div>
          )}

          {currentStep < steps.length - 1 ? (
            <span
              onClick={() => {
                if (isNextButtonDisabled && !isNextButtonLoading) {
                  ctx.triggerShake();
                  const stepErrors = ctx.getActiveErrors(stepScope);
                  if (stepErrors.length > 0) {
                    setTimeout(() => focusField(stepErrors[0].field), 100);
                  }
                }
              }}
            >
              <Button
                onClick={handleNext}
                disabled={isNextButtonLoading || isNextButtonDisabled}
                className={cn(isNextButtonDisabled && !isNextButtonLoading && "opacity-50")}
              >
                Next
              </Button>
            </span>
          ) : (
            <Button onClick={handleSave} disabled={isLoading || (isLoadingDefaults && !isEditing)}>
              {isLoading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Client')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

ClientFormInner.displayName = 'ClientFormInner';

/** Outer component: builds schemaMap from state and wraps in ValidationProvider. */
const ClientForm = forwardRef<HTMLDivElement, ClientFormProps>((props, ref) => {
  const {
    clientData,
    isEditing = false,
    allClients = [],
    onNameChange,
  } = props;

  // Lift form state to the outer level so the schemaMap can reference formData.
  // The inner component receives the state as props rather than calling the hook itself.
  const {
    formData,
    setFormData,
    isTravelManuallyEnabled,
    setIsTravelManuallyEnabled,
    isAiGeneratedDescription,
    setIsAiGeneratedDescription,
    userDefaults,
    isLoadingDefaults,
    granularCustomizedFields,
    customizedSections,
  } = useClientFormState({
    clientData,
    isEditing,
    onNameChange,
    allClients,
  });

  const schemaMap = useClientSchemaMap({
    formData,
    customizedSections,
    isTravelManuallyEnabled,
    allClients,
    isEditing,
  });

  return (
    <ValidationProvider
      showToasts={true}
      showSuccessFeedback={false}
      schemaMap={schemaMap}
    >
      <ClientFormInner
        ref={ref}
        {...props}
        formData={formData}
        setFormData={setFormData}
        isTravelManuallyEnabled={isTravelManuallyEnabled}
        setIsTravelManuallyEnabled={setIsTravelManuallyEnabled}
        isAiGeneratedDescription={isAiGeneratedDescription}
        setIsAiGeneratedDescription={setIsAiGeneratedDescription}
        userDefaults={userDefaults ?? undefined}
        isLoadingDefaults={isLoadingDefaults}
        granularCustomizedFields={granularCustomizedFields}
        customizedSections={customizedSections}
      />
    </ValidationProvider>
  );
});

ClientForm.displayName = 'ClientForm';
export default ClientForm;
